import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { AvatarUpload } from '../components/AvatarUpload';
import { ArrowLeft, Save, Building2, Mail, User, Briefcase, MapPin, Phone, Globe } from 'lucide-react';
interface ManagerProfile {
  name: string;
  email: string;
  avatar: string;
  company: string;
  role: string;
  bio: string;
  phone: string;
  location: string;
  website: string;
}
export function ManagerProfilePage() {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [profile, setProfile] = useState<ManagerProfile>({
    name: 'Talent Management Inc.',
    email: 'contact@talentmgmt.com',
    avatar: '',
    company: 'Talent Management Inc.',
    role: 'Agency Manager',
    bio: 'Leading influencer management agency specializing in fashion, lifestyle, and tech creators. 10+ years of experience in digital marketing and brand partnerships.',
    phone: '+1 (555) 123-4567',
    location: 'Los Angeles, CA',
    website: 'www.talentmgmt.com'
  });
  const handleSave = async () => {
    setIsSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    localStorage.setItem('manager_profile', JSON.stringify(profile));
    setIsSaving(false);
    navigate('/manager');
  };
  const handleAvatarChange = (avatarUrl: string) => {
    setProfile({
      ...profile,
      avatar: avatarUrl
    });
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 pb-12" data-id="element-1137">
      <header className="bg-white/95 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-10 shadow-sm" data-id="element-1138">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-1139">
          <div className="flex items-center" data-id="element-1140">
            <Button variant="ghost" size="sm" className="mr-4" onClick={() => navigate('/manager')} data-id="element-1141">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-1142" /> Back
            </Button>
            <h1 className="text-xl font-bold text-slate-900" data-id="element-1143">
              Profile Settings
            </h1>
          </div>
          <Button variant="premium" size="sm" onClick={handleSave} disabled={isSaving} className="min-w-[120px]" data-id="element-1144">
            {isSaving ? <span className="flex items-center" data-id="element-1145">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1146">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1147"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1148"></path>
                </svg>
                Saving...
              </span> : <>
                <Save className="h-4 w-4 mr-2" data-id="element-1149" />
                Save Changes
              </>}
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1150">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1151">
          {/* Left Column - Avatar & Quick Info */}
          <div className="lg:col-span-1 space-y-6" data-id="element-1152">
            <Card variant="premium" data-id="element-1153">
              <CardContent className="p-8" data-id="element-1154">
                <div className="text-center mb-6" data-id="element-1155">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-1156">
                    Profile Photo
                  </h3>
                  <p className="text-sm text-slate-600" data-id="element-1157">
                    Upload a professional photo
                  </p>
                </div>
                <AvatarUpload currentAvatar={profile.avatar} onAvatarChange={handleAvatarChange} size="xl" data-id="element-1158" />
              </CardContent>
            </Card>

            <Card variant="premium" data-id="element-1159">
              <CardHeader data-id="element-1160">
                <CardTitle className="text-base" data-id="element-1161">Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-1162">
                <div className="flex items-center space-x-3 text-sm" data-id="element-1163">
                  <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center" data-id="element-1164">
                    <Building2 className="h-5 w-5 text-primary-600" data-id="element-1165" />
                  </div>
                  <div data-id="element-1166">
                    <p className="text-xs text-slate-500" data-id="element-1167">Company</p>
                    <p className="font-medium text-slate-900" data-id="element-1168">
                      {profile.company}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm" data-id="element-1169">
                  <div className="h-10 w-10 bg-secondary-100 rounded-lg flex items-center justify-center" data-id="element-1170">
                    <Briefcase className="h-5 w-5 text-secondary-600" data-id="element-1171" />
                  </div>
                  <div data-id="element-1172">
                    <p className="text-xs text-slate-500" data-id="element-1173">Role</p>
                    <p className="font-medium text-slate-900" data-id="element-1174">{profile.role}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm" data-id="element-1175">
                  <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-1176">
                    <MapPin className="h-5 w-5 text-green-600" data-id="element-1177" />
                  </div>
                  <div data-id="element-1178">
                    <p className="text-xs text-slate-500" data-id="element-1179">Location</p>
                    <p className="font-medium text-slate-900" data-id="element-1180">
                      {profile.location}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Profile Form */}
          <div className="lg:col-span-2 space-y-6" data-id="element-1181">
            <Card variant="premium" data-id="element-1182">
              <CardHeader data-id="element-1183">
                <CardTitle data-id="element-1184">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6" data-id="element-1185">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1186">
                  <div className="space-y-2" data-id="element-1187">
                    <label className="text-sm font-semibold text-slate-700 flex items-center" data-id="element-1188">
                      <User className="h-4 w-4 mr-2 text-slate-400" data-id="element-1189" />
                      Full Name
                    </label>
                    <Input value={profile.name} onChange={e => setProfile({
                    ...profile,
                    name: e.target.value
                  })} className="input-premium" placeholder="Enter your name" data-id="element-1190" />
                  </div>
                  <div className="space-y-2" data-id="element-1191">
                    <label className="text-sm font-semibold text-slate-700 flex items-center" data-id="element-1192">
                      <Mail className="h-4 w-4 mr-2 text-slate-400" data-id="element-1193" />
                      Email Address
                    </label>
                    <Input type="email" value={profile.email} onChange={e => setProfile({
                    ...profile,
                    email: e.target.value
                  })} className="input-premium" placeholder="your@email.com" data-id="element-1194" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1195">
                  <div className="space-y-2" data-id="element-1196">
                    <label className="text-sm font-semibold text-slate-700 flex items-center" data-id="element-1197">
                      <Phone className="h-4 w-4 mr-2 text-slate-400" data-id="element-1198" />
                      Phone Number
                    </label>
                    <Input value={profile.phone} onChange={e => setProfile({
                    ...profile,
                    phone: e.target.value
                  })} className="input-premium" placeholder="+1 (555) 000-0000" data-id="element-1199" />
                  </div>
                  <div className="space-y-2" data-id="element-1200">
                    <label className="text-sm font-semibold text-slate-700 flex items-center" data-id="element-1201">
                      <MapPin className="h-4 w-4 mr-2 text-slate-400" data-id="element-1202" />
                      Location
                    </label>
                    <Input value={profile.location} onChange={e => setProfile({
                    ...profile,
                    location: e.target.value
                  })} className="input-premium" placeholder="City, Country" data-id="element-1203" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-1204">
                  <label className="text-sm font-semibold text-slate-700 flex items-center" data-id="element-1205">
                    <Globe className="h-4 w-4 mr-2 text-slate-400" data-id="element-1206" />
                    Website
                  </label>
                  <Input value={profile.website} onChange={e => setProfile({
                  ...profile,
                  website: e.target.value
                })} className="input-premium" placeholder="www.yourwebsite.com" data-id="element-1207" />
                </div>
              </CardContent>
            </Card>

            <Card variant="premium" data-id="element-1208">
              <CardHeader data-id="element-1209">
                <CardTitle data-id="element-1210">Company Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6" data-id="element-1211">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1212">
                  <div className="space-y-2" data-id="element-1213">
                    <label className="text-sm font-semibold text-slate-700 flex items-center" data-id="element-1214">
                      <Building2 className="h-4 w-4 mr-2 text-slate-400" data-id="element-1215" />
                      Company Name
                    </label>
                    <Input value={profile.company} onChange={e => setProfile({
                    ...profile,
                    company: e.target.value
                  })} className="input-premium" placeholder="Your Company" data-id="element-1216" />
                  </div>
                  <div className="space-y-2" data-id="element-1217">
                    <label className="text-sm font-semibold text-slate-700 flex items-center" data-id="element-1218">
                      <Briefcase className="h-4 w-4 mr-2 text-slate-400" data-id="element-1219" />
                      Your Role
                    </label>
                    <Input value={profile.role} onChange={e => setProfile({
                    ...profile,
                    role: e.target.value
                  })} className="input-premium" placeholder="e.g., Agency Manager" data-id="element-1220" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-1221">
                  <label className="text-sm font-semibold text-slate-700" data-id="element-1222">
                    Bio / About
                  </label>
                  <textarea value={profile.bio} onChange={e => setProfile({
                  ...profile,
                  bio: e.target.value
                })} className="w-full h-32 px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-white resize-none" placeholder="Tell us about yourself and your company..." data-id="element-1223" />
                  <p className="text-xs text-slate-500" data-id="element-1224">
                    {profile.bio.length} / 500 characters
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Save Button (Mobile) */}
            <div className="lg:hidden" data-id="element-1225">
              <Button variant="premium" className="w-full h-12" onClick={handleSave} disabled={isSaving} data-id="element-1226">
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>;
}